import { Fragment, useEffect, useState } from 'react';
import { useTenantScope } from '../context/TenantScopeContext';
import axios from 'axios';
import { Plus, Pencil, Check, X, UserX, UserCheck, Trash2, KeyRound, ShieldOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { ColumnDef, DraggableTh, useColumnPrefs } from '../components/ColumnPicker';

interface User {
  id: number;
  name: string;
  email: string;
  scope_level: 'platform' | 'partner' | 'tenant';
  role: 'admin' | 'analyst' | 'viewer';
  partner_id: number | null;
  tenant_id: number | null;
  partner_name: string | null;
  tenant_name: string | null;
  active: boolean;
  totp_enabled: boolean;
}

interface Partner { id: number; name: string }
interface Tenant { id: number; name: string; partner_id: number }

const USER_COLUMNS: ColumnDef[] = [
  { key: 'name', label: 'Name', defaultVisible: true },
  { key: 'email', label: 'Email', defaultVisible: true },
  { key: 'scope', label: 'Scope', defaultVisible: true },
  { key: 'role', label: 'Role', defaultVisible: true },
  { key: 'organization', label: 'Partner / Tenant', defaultVisible: true },
  { key: 'status', label: 'Status', defaultVisible: true },
  { key: 'twofa', label: '2FA', defaultVisible: true },
  { key: 'actions', label: 'Actions', defaultVisible: true },
];

const SCOPE_LABEL: Record<string, string> = { platform: 'Platform', partner: 'Partner', tenant: 'Tenant' };
const SCOPE_BADGE: Record<string, string> = {
  platform: 'bg-indigo-100 text-indigo-700', partner: 'bg-cyan-100 text-cyan-700', tenant: 'bg-gray-100 text-gray-600',
};

type SortKey = 'last_name' | 'name' | 'email';
type GroupKey = 'none' | 'partner' | 'tenant';

// La tabella users ha solo il nome completo: il cognome è l'ultima parola (scelta del
// 2026-10-27, tutti gli utenti persona sono "Nome Cognome"; gli account di ruolo come
// "Direzione Banca" finiscono ordinati per l'ultima parola).
const lastName = (name: string) => name.trim().split(/\s+/).pop() ?? '';
const compareText = (a: string, b: string) => a.localeCompare(b, 'it', { sensitivity: 'base' });
const SORT_VALUE: Record<SortKey, (u: User) => string> = {
  last_name: u => lastName(u.name), name: u => u.name, email: u => u.email,
};
// Gruppo di un utente; null = senza partner/tenant (utenti platform, o partner se si raggruppa per tenant).
const GROUP_VALUE: Record<Exclude<GroupKey, 'none'>, (u: User) => string | null> = {
  partner: u => u.partner_name, tenant: u => u.tenant_name,
};
const NO_GROUP_LABEL: Record<Exclude<GroupKey, 'none'>, string> = {
  partner: 'No partner (platform users)', tenant: 'No tenant (platform / partner users)',
};

// Utenti visibili secondo la stessa gerarchia usata ovunque nell'app: un platform admin vede
// tutti, un utente partner vede i propri utenti + quelli dei propri tenant clienti, un utente
// tenant vede solo gli utenti del proprio tenant (accesso ai soli dati del proprio sito/cliente).
export default function UsersPage() {
  const { user: me, isAdmin } = useAuth();
  // Tenant selezionato nella console: per un platform admin restringe la lista agli utenti del
  // partner di quel tenant (2026-10-24); per partner e tenant non cambia nulla (già scoped).
  const { selectedTenantId, selectedTenantName } = useTenantScope();
  const { orderedDefs, moveColumn } = useColumnPrefs('soc.columns.users', USER_COLUMNS);
  const [users, setUsers] = useState<User[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [scopeLevel, setScopeLevel] = useState<'platform' | 'partner' | 'tenant'>(
    me?.scopeLevel === 'tenant' ? 'tenant' : me?.scopeLevel === 'partner' ? 'partner' : 'partner'
  );
  const [role, setRole] = useState<'admin' | 'analyst' | 'viewer'>('viewer');
  const [partnerId, setPartnerId] = useState<string>(me?.partnerId ? String(me.partnerId) : '');
  const [tenantId, setTenantId] = useState<string>(me?.tenantId ? String(me.tenantId) : '');

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState<'admin' | 'analyst' | 'viewer'>('viewer');
  const [resettingId, setResettingId] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('last_name');
  const [groupBy, setGroupBy] = useState<GroupKey>('none');

  async function load() {
    setLoading(true);
    const requests: Promise<any>[] = [axios.get('/api/users', { params: { tenantId: selectedTenantId ?? undefined } })];
    if (me?.scopeLevel === 'platform') requests.push(axios.get('/api/partners'));
    if (me?.scopeLevel !== 'tenant') requests.push(axios.get('/api/tenants'));
    const results = await Promise.all(requests);
    setUsers(results[0].data.data);
    let idx = 1;
    if (me?.scopeLevel === 'platform') setPartners(results[idx++].data.data);
    if (me?.scopeLevel !== 'tenant') setTenants(results[idx++].data.data);
    setLoading(false);
  }

  useEffect(() => { load(); }, [selectedTenantId]);

  // Partner del tenant selezionato (solo platform admin): intestazione e preselezione del form.
  const scopedPartnerId = me?.scopeLevel === 'platform' && selectedTenantId
    ? tenants.find(t => t.id === selectedTenantId)?.partner_id ?? null : null;
  const scopedPartnerName = scopedPartnerId ? partners.find(p => p.id === scopedPartnerId)?.name ?? null : null;
  useEffect(() => {
    if (scopedPartnerId) { setPartnerId(String(scopedPartnerId)); setTenantId(selectedTenantId ? String(selectedTenantId) : ''); }
  }, [scopedPartnerId, selectedTenantId]);

  const scopeOptions = me?.scopeLevel === 'platform' ? ['platform', 'partner', 'tenant']
    : me?.scopeLevel === 'partner' ? ['partner', 'tenant'] : ['tenant'];
  const tenantOptions = me?.scopeLevel === 'platform' && partnerId
    ? tenants.filter(t => String(t.partner_id) === partnerId)
    : tenants;

  const sortedUsers = [...users].sort((a, b) =>
    compareText(SORT_VALUE[sortBy](a), SORT_VALUE[sortBy](b)) || compareText(a.name, b.name));
  // Gruppi in ordine alfabetico, quello senza partner/tenant in fondo; dentro ogni gruppo resta l'ordinamento scelto.
  const groups: { label: string | null; users: User[] }[] = [];
  if (groupBy === 'none') {
    groups.push({ label: null, users: sortedUsers });
  } else {
    const byGroup = new Map<string | null, User[]>();
    for (const u of sortedUsers) {
      const key = GROUP_VALUE[groupBy](u);
      byGroup.set(key, [...(byGroup.get(key) ?? []), u]);
    }
    const keys = [...byGroup.keys()].sort((a, b) => a === null ? 1 : b === null ? -1 : compareText(a, b));
    for (const key of keys) groups.push({ label: key ?? NO_GROUP_LABEL[groupBy], users: byGroup.get(key)! });
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await axios.post('/api/users', {
        name, email, password, scopeLevel, role,
        partnerId: scopeLevel === 'platform' ? undefined : Number(me?.scopeLevel === 'partner' ? me.partnerId : partnerId),
        tenantId: scopeLevel === 'tenant' ? Number(me?.scopeLevel === 'tenant' ? me.tenantId : tenantId) : undefined,
      });
      setName(''); setEmail(''); setPassword(''); setShowForm(false);
      load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error while creating the user');
    }
  }

  function startEdit(u: User) {
    setEditingId(u.id);
    setEditName(u.name);
    setEditRole(u.role);
  }

  async function saveEdit(id: number) {
    await axios.put(`/api/users/${id}`, { name: editName, role: editRole });
    setEditingId(null);
    load();
  }

  async function toggleActive(u: User) {
    await axios.put(`/api/users/${u.id}/${u.active ? 'deactivate' : 'reactivate'}`);
    load();
  }

  async function removeUser(u: User) {
    if (!window.confirm(`Delete user "${u.name}"? This cannot be undone.`)) return;
    await axios.delete(`/api/users/${u.id}`);
    load();
  }

  async function resetPassword(id: number) {
    if (!newPassword.trim()) return;
    await axios.put(`/api/users/${id}`, { password: newPassword });
    setResettingId(null);
    setNewPassword('');
  }

  // Copre il caso in cui l'utente abbia perso sia il dispositivo con l'app authenticator sia i
  // codici di recupero: senza questo, sarebbe bloccato fuori definitivamente (la password da sola
  // non basta più a fare login se totp_enabled è true). L'utente dovrà rifare il setup del 2FA
  // dal proprio profilo (Security) al prossimo accesso.
  async function reset2fa(u: User) {
    if (!window.confirm(`Reset two-factor authentication for "${u.name}"? They will need to set it up again.`)) return;
    await axios.post(`/api/users/${u.id}/reset-2fa`);
    load();
  }

  function renderCell(u: User, key: string) {
    const editing = editingId === u.id;
    switch (key) {
      case 'name':
        return editing
          ? <input value={editName} onChange={e => setEditName(e.target.value)} className="input-field py-1 text-xs w-40" />
          : <span className="font-medium text-gray-800">{u.name}</span>;
      case 'email':
        return <span className="text-gray-500">{u.email}</span>;
      case 'scope':
        return <span className={`badge ${SCOPE_BADGE[u.scope_level]}`}>{SCOPE_LABEL[u.scope_level]}</span>;
      case 'role':
        return editing ? (
          <select value={editRole} onChange={e => setEditRole(e.target.value as any)} className="input-field py-1 text-xs">
            <option value="admin">admin</option>
            <option value="analyst">analyst</option>
            <option value="viewer">viewer</option>
          </select>
        ) : <span className="text-gray-500 capitalize">{u.role}</span>;
      case 'organization':
        return <span className="text-gray-500">{[u.partner_name, u.tenant_name].filter(Boolean).join(' · ') || '—'}</span>;
      case 'status':
        return u.active
          ? <span className="badge bg-green-100 text-green-700">Active</span>
          : <span className="badge bg-gray-100 text-gray-500">Disabled</span>;
      case 'twofa':
        return u.totp_enabled
          ? <span className="badge bg-emerald-100 text-emerald-700">Enabled</span>
          : <span className="badge bg-gray-100 text-gray-500">Disabled</span>;
      case 'actions':
        return editing ? (
          <span className="inline-flex gap-1">
            <button onClick={() => saveEdit(u.id)} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check size={14} /></button>
            <button onClick={() => setEditingId(null)} className="p-1 text-gray-400 hover:bg-gray-100 rounded"><X size={14} /></button>
          </span>
        ) : !isAdmin ? <span className="text-gray-300">—</span> : (
          <span className="inline-flex gap-1">
            <button onClick={() => startEdit(u)} title="Edit" className="p-1 text-gray-400 hover:text-primary-600 hover:bg-gray-50 rounded"><Pencil size={13} /></button>
            <button onClick={() => setResettingId(u.id)} title="Reset password" className="p-1 text-gray-400 hover:text-primary-600 hover:bg-gray-50 rounded"><KeyRound size={13} /></button>
            {u.totp_enabled && (
              <button onClick={() => reset2fa(u)} title="Reset 2FA" className="p-1 text-gray-400 hover:text-amber-600 hover:bg-gray-50 rounded"><ShieldOff size={13} /></button>
            )}
            <button onClick={() => toggleActive(u)} title={u.active ? 'Disable' : 'Reactivate'}
              className="p-1 text-gray-400 hover:text-amber-600 hover:bg-gray-50 rounded">
              {u.active ? <UserX size={13} /> : <UserCheck size={13} />}
            </button>
            <button onClick={() => removeUser(u)} disabled={u.id === me?.id} title="Delete"
              className="p-1 text-gray-400 hover:text-red-600 hover:bg-gray-50 rounded disabled:opacity-30 disabled:cursor-not-allowed"><Trash2 size={13} /></button>
          </span>
        );
      default:
        return null;
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            Users{scopedPartnerName ? ` \u2014 ${scopedPartnerName}` : ''}
          </h1>
          <p className="text-sm text-gray-500">
            {scopedPartnerName
              ? `Users of partner ${scopedPartnerName} (selected tenant: ${selectedTenantName || ''}). Choose "All tenants" to see every user.`
              : "Partners see only their tenants' users; tenant users see only their own site's users."}
          </p>
        </div>
        {isAdmin && (
<button onClick={() => setShowForm(v => !v)} className="btn-primary flex items-center gap-1">
          <Plus size={16} /> New user
        </button>
)}
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="card p-4 mb-4 flex flex-wrap gap-3 items-end">
          {error && <div className="w-full text-sm text-red-600">{error}</div>}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Name</label>
            <input value={name} onChange={e => setName(e.target.value)} required className="input-field" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="input-field" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required className="input-field" />
          </div>
          {scopeOptions.length > 1 && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Scope</label>
              <select value={scopeLevel} onChange={e => setScopeLevel(e.target.value as any)} className="input-field">
                {scopeOptions.map(s => <option key={s} value={s}>{SCOPE_LABEL[s]}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Role</label>
            <select value={role} onChange={e => setRole(e.target.value as any)} className="input-field">
              <option value="admin">Admin</option>
              <option value="analyst">Analyst</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
          {me?.scopeLevel === 'platform' && scopeLevel !== 'platform' && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Partner</label>
              <select value={partnerId} onChange={e => { setPartnerId(e.target.value); setTenantId(''); }} required className="input-field">
                <option value="">Select…</option>
                {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}
          {scopeLevel === 'tenant' && me?.scopeLevel !== 'tenant' && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Tenant</label>
              <select value={tenantId} onChange={e => setTenantId(e.target.value)} required className="input-field">
                <option value="">Select…</option>
                {tenantOptions.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
          <button type="submit" className="btn-primary">Create</button>
        </form>
      )}

      <div className="flex flex-wrap gap-3 items-end mb-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Sort by</label>
          <select value={sortBy} onChange={e => setSortBy(e.target.value as SortKey)} className="input-field py-1 text-sm">
            <option value="last_name">Last name</option>
            <option value="name">First name</option>
            <option value="email">Email</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Group by</label>
          <select value={groupBy} onChange={e => setGroupBy(e.target.value as GroupKey)} className="input-field py-1 text-sm">
            <option value="none">None</option>
            <option value="partner">Partner</option>
            <option value="tenant">Tenant</option>
          </select>
        </div>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr>
              {orderedDefs.map(d => (
                <DraggableTh key={d.key} colKey={d.key} onMove={moveColumn} className={`table-header ${d.key === 'actions' ? 'text-right' : ''}`}>
                  {d.label}
                </DraggableTh>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td className="table-cell text-gray-400" colSpan={orderedDefs.length}>Loading…</td></tr>}
            {!loading && users.length === 0 && (
              <tr><td className="table-cell text-gray-400" colSpan={orderedDefs.length}>No user created yet</td></tr>
            )}
            {groups.map(g => (
              <Fragment key={g.label ?? 'all'}>
              {g.label !== null && (
                <tr className="border-t border-gray-200 bg-gray-50">
                  <td className="table-cell font-semibold text-gray-700" colSpan={orderedDefs.length}>
                    {g.label} <span className="font-normal text-gray-400">({g.users.length})</span>
                  </td>
                </tr>
              )}
              {g.users.map(u => (
              <tr key={u.id} className={`border-t border-gray-100 hover:bg-gray-50 ${!u.active ? 'opacity-50' : ''}`}>
                {orderedDefs.map(d => (
                  <td key={d.key} className={`table-cell ${d.key === 'actions' ? 'text-right' : ''}`}>
                    {resettingId === u.id && d.key === 'actions' ? (
                      <span className="inline-flex items-center gap-1">
                        <input type="password" autoFocus value={newPassword} onChange={e => setNewPassword(e.target.value)}
                          placeholder="New password" className="input-field py-1 text-xs w-32" />
                        <button onClick={() => resetPassword(u.id)} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check size={14} /></button>
                        <button onClick={() => { setResettingId(null); setNewPassword(''); }} className="p-1 text-gray-400 hover:bg-gray-100 rounded"><X size={14} /></button>
                      </span>
                    ) : renderCell(u, d.key)}
                  </td>
                ))}
              </tr>
              ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
